import {
  Form,
  Button,
  Select,
  Flex,
  theme,
  SelectProps,
  message,
  Spin,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/utilities";
import { API_BASE_URL, API_URL_PERMISSIONS } from "@/services/XCONS_ENV";
import { Typography } from "antd";
import { MailOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { debounce } from "@/utilities/debounce";

const { Text } = Typography;
const { Option } = Select;

export const StoresUserPermissionsCopy: React.FC<{
  closeModal?: () => void;
  sellerCode?: string;
  t?: any;
  usersData: any;
  permissions?: any;
  configs?: any;
  typeIdentifier?: string;
}> = ({
  sellerCode,
  t,
  usersData,
  closeModal,
  permissions,
  configs,
  typeIdentifier = "store",
}) => {
    const { token } = theme.useToken();
    const [structureLoading, setStructureLoading] = useState<boolean>(false);
    const [userCopyTo, setUserCopyTo] = useState<string[]>([]);
    const [loadingOperators, setLoadingOperators] = useState(false);
    const [operators, setOperators] = useState<any>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [pagination, setPagination] = useState({
      currentPage: 1,
      totalPages: 1,
      pageSize: 20,
    });

    const handleCancel = () => {
      if (closeModal) {
        closeModal();
      }
    };

    const getOperators = async (loadMore: boolean = false) => {
      if (loadingOperators) return;

      setLoadingOperators(true);
      try {
        const { currentPage, pageSize } = loadMore
          ? { ...pagination, currentPage: pagination.currentPage + 1 }
          : pagination;

        const url = `${API_BASE_URL}/permissions/${typeIdentifier}/${sellerCode}/users?page=${currentPage}&limit=${pageSize}&search=${encodeURIComponent(
          searchTerm,
        )}`;

        const response = await axiosInstance.get(url);

        setOperators((prev: any) =>
          loadMore ? [...prev, ...response.data.data] : response.data.data,
        );

        setPagination({
          currentPage,
          totalPages: response.data.meta.totalPages,
          pageSize,
        });
      } catch (error) {
        console.error("Error al cargar contactos:", error);
      } finally {
        setLoadingOperators(false);
      }
    };

    const debouncedSearch = useRef(
      debounce((value: string) => {
        setSearchTerm(value);
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }, 300),
    ).current;

    const handleSearch = (value: string) => {
      debouncedSearch(value);
    };

    useEffect(() => {
      getOperators();
    }, []);

    const handleSave = async () => {
      setStructureLoading(true);
      try {
        // Ser asistente IA no es un permiso que se copia (D62–D63): marcaría como asistentes a
        // personas, o desmarcaría al asistente. El guardado fusiona, así que sin `agent` en el
        // payload cada destino conserva el suyo.
        const sinAsistente = Array.isArray(permissions)
          ? permissions.filter((p: { code?: string }) => p?.code !== "agent")
          : permissions;
        const requests = userCopyTo.map((userId) => {
          const payload = {
            type: typeIdentifier,
            identifier: sellerCode,
            userId: userId,
            permissions: sinAsistente,
            configs: configs,
          };
          return axiosInstance.post(API_URL_PERMISSIONS, payload);
        });

        await Promise.all(requests);

        message.success(t("message.configurationSucccess"));
        handleCancel();
      } catch (error: any) {
        console.error(t("message.configurationErrorUpdating"), error);
        const apiMsg =
          error?.response?.data?.message ?? error?.response?.data?.error;
        message.error(
          Array.isArray(apiMsg)
            ? apiMsg.join(", ")
            : apiMsg || t("message.configurationErrorUpdating"),
        );
      } finally {
        setStructureLoading(false);
      }
    };

    const handlePopupScroll = async (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.5;

      if (
        isNearBottom &&
        pagination.currentPage < pagination.totalPages &&
        !loadingOperators
      ) {
        await getOperators(true);
      }
    };

    useEffect(() => {
      setOperators([]);
      getOperators(false);
    }, [searchTerm]);

    return (
      // LateralModal no le pone margen al body: lo pone cada panel (xcons-docs#509).
      <Form layout="vertical" style={{ padding: "16px 24px" }}>
        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("userPermissions.select.titleFrom")}
            </span>
          }
          name="copy_from"
        >
          <Text style={{ marginLeft: 6 }}>
            {usersData?.firstname} {usersData?.lastname}
          </Text>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("userPermissions.select.titleTo")}
            </span>
          }
          name="copy_to"
          rules={[{ required: true, message: t("message.error.inputEmpty") }]}
        >
          <Select
            placeholder={t("labeldropdown.selectoperator")}
            mode="multiple"
            loading={loadingOperators}
            showSearch
            allowClear
            onSearch={handleSearch}
            onPopupScroll={handlePopupScroll}
            style={{ width: "100%" }}
            onDropdownVisibleChange={(open) => {
              if (open && operators.length === 0) {
                getOperators(false);
              }
            }}
            optionLabelProp="label"
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent={
              loadingOperators ? (
                <Flex justify="center" align="center" style={{ padding: 16 }}>
                  <Spin size="small" />
                </Flex>
              ) : (
                <div style={{ padding: 16, textAlign: "center" }}>
                  {searchTerm
                    ? t('label.no.operators', { searchTerm })
                    : "No operators available"}
                </div>
              )
            }
            onChange={(value) => {
              setUserCopyTo(value);
            }}
          >
            {operators.map((operator: any) => (
              <Option
                key={operator?.user?.userId}
                value={operator?.user?.userId}
                label={`${operator?.user?.firstname} ${operator?.user?.lastname}`}
              >
                <Flex vertical gap={4}>
                  <Text strong style={{ fontSize: 16 }}>
                    {operator?.user?.firstname} {operator?.user?.lastname}
                  </Text>
                  <Flex gap={8} align="center">
                    <MailOutlined style={{ fontSize: 11 }} />
                    <Text type="secondary">{operator?.user?.email || "-"}</Text>
                  </Flex>
                  <Flex gap={8} align="center">
                    <WhatsAppOutlined style={{ fontSize: 11 }} />
                    <Text type="secondary">
                      {operator?.user?.whatsapp || "-"}
                    </Text>
                  </Flex>
                </Flex>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Flex
          align="center"
          justify="end"
          gap="8px"
          style={{ position: "absolute", bottom: 48, right: 24 }}
        >
          <Button
            type="default"
            onClick={() => {
              handleCancel();
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            htmlType="button"
            type="primary"
            onClick={handleSave}
            loading={structureLoading}
            disabled={!userCopyTo.length}
          >
            {t("buttons.save")}
          </Button>
        </Flex>
      </Form>
    );
  };
